import {
  BigDotIcon,
  BlockUserIcon,
  CheckIcon,
  GKButton,
  GKModal,
  GKSearchSelect,
  GKTextField,
  Grid2,
  RupeeIcon,
  Show,
  StatusField,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "components";
import { map } from "lodash";
import { Controller, useForm } from "react-hook-form";
import {
  useCancelMandateMutation,
  useCreateMandateMutation,
  useFetchMandateByIdQuery,
} from "store/features/Revenue/ProformaApi";
import { useAppSelector } from "store/store";
import { matchDate } from "utils/helpers";
import { moneyCommaSeparator, numberToCurrency } from "utils/MathFunction";
import { Cell, TableBodyCell } from "../utils";

interface Props {
  open: { open: boolean; showDetails: boolean; mandateId: string };
  setOpen: (open: {
    open: boolean;
    showDetails: boolean;
    mandateId: string;
  }) => void;
}

const MandateFormModal = (props: Props) => {
  const { open, setOpen } = props;

  const portfolios = useAppSelector((state) => state?.app?.portfolios);

  const form = useForm({
    defaultValues: { portfolio: "", planMaxAmount: "" },
  });

  const { data, isFetching } = useFetchMandateByIdQuery(open?.mandateId, {
    skip: !open?.mandateId,
  });

  const [createMandate, { isLoading }] = useCreateMandateMutation();

  const [cancelMandate, { isLoading: cancelMandateLoading }] =
    useCancelMandateMutation();

  return (
    <GKModal
      open={open?.open}
      setOpen={() =>
        setOpen({ open: false, showDetails: false, mandateId: null })
      }
      modalTitle="Create Mandate"
      width={open?.showDetails ? "800px" : "450px"}
      loading={isFetching}
      footer={
        !open?.showDetails && (
          <Grid2 size={12} container justifyContent={"space-between"}>
            <GKButton
              variant="outlined"
              color="dark"
              onClick={() =>
                setOpen({ open: false, showDetails: false, mandateId: null })
              }
            >
              Cancel
            </GKButton>
            <GKButton
              variant="contained"
              startIcon={<CheckIcon />}
              form="mandate-form"
              type="submit"
              loading={isLoading}
            >
              Save & Create Mandate
            </GKButton>
          </Grid2>
        )
      }
    >
      <>
        <Show.When isTrue={!open?.showDetails}>
          <form
            id="mandate-form"
            onSubmit={form.handleSubmit(async (values) => {
              try {
                await createMandate(values).unwrap();
                setOpen({ open: false, showDetails: false, mandateId: null });
              } catch (err) {
                console.error("Create mandate failed:", err);
              }
            })}
          >
            <Grid2 container py={1} spacing={3}>
              <Grid2 size={12}>
                <Controller
                  name="portfolio"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please select portfolio",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKSearchSelect
                      {...field}
                      inputLabel="Portfolio Name"
                      placeholder="Select Portfolio Name"
                      disableClearable={false}
                      options={portfolios}
                      onChange={(e, val) => {
                        field.onChange(val?.value);
                      }}
                      value={
                        portfolios.find(
                          (dataItem) => dataItem.value === field.value,
                        ) || ""
                      }
                      getOptionLabel={(option) => option.name || option}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="planMaxAmount"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please enter max amount",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      inputLabel="Max Amount"
                      slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>
          </form>
        </Show.When>
        <Show.When isTrue={open?.showDetails}>
          <Grid2 container pt={1} spacing={4}>
            <Grid2
              container
              size={12}
              p={2}
              borderRadius={2}
              bgcolor={"var(--grey-bg-100)"}
              spacing={"12px"}
            >
              <Grid2 container size={8} spacing={"4px"} pb={"12px"}>
                <Grid2 size={12}>
                  <Typography
                    fontSize={16}
                    fontWeight={600}
                    color="var(--black)"
                  >
                    {data?.data?.portfolioName || "-"}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    color="var(--neutral-500)"
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                  >
                    Payment Method:
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      color="var(--black)"
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                    >
                      Mandate
                      <Typography>
                        <StatusField
                          statusString={data?.data?.subscriptionStatus}
                        />
                      </Typography>
                    </Typography>
                  </Typography>
                </Grid2>
              </Grid2>
              <Grid2
                size={4}
                display={"flex"}
                justifyContent={"flex-end"}
                alignItems={"flex-start"}
                pb={"12px"}
              >
                <GKButton
                  color="error"
                  disabled={
                    cancelMandateLoading ||
                    data?.data?.subscriptionStatus === "CANCELLED"
                  }
                  startIcon={<BlockUserIcon />}
                  onClick={async () => {
                    try {
                      await cancelMandate(open?.mandateId).unwrap();
                      setOpen({
                        open: false,
                        showDetails: false,
                        mandateId: null,
                      });
                    } catch (err) {
                      console.error("Create mandate failed:", err);
                    }
                  }}
                >
                  Cancel Mandate
                </GKButton>
              </Grid2>
              <Grid2 size={12}>
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                  fontSize={14}
                  fontWeight={500}
                  color="var(--neutral-500)"
                >
                  Amount:
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    color="var(--black)"
                  >
                    {numberToCurrency(data?.data?.planMaxAmount)}
                  </Typography>
                </Typography>
              </Grid2>
              <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                  fontSize={14}
                  fontWeight={500}
                  color="var(--neutral-500)"
                >
                  Start Date:
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    color="var(--black)"
                  >
                    {matchDate(data?.data?.subscriptionStartDate)}
                  </Typography>
                </Typography>
                <BigDotIcon color="var(--grey-100)" height={22} width={22} />
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                  fontSize={14}
                  fontWeight={500}
                  color="var(--neutral-500)"
                >
                  Last Renewal Date:
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    color="var(--black)"
                  >
                    {matchDate(data?.data?.subscriptionExpiryTime)}
                  </Typography>
                </Typography>
              </Grid2>
            </Grid2>
            <Show.When
              isTrue={data?.data?.subscriptionPaymentHistory?.length > 0}
            >
              <Grid2 container size={12} spacing={2}>
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={600}
                    color="var(--neutral-500)"
                    textTransform={"uppercase"}
                  >
                    Mandate history
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          "&.MuiTableRow-head th": { p: "12px" },
                        }}
                      >
                        <Cell align="left">settlement date</Cell>
                        <Cell align="right">amount (₹)</Cell>
                        <Cell align="left">payment status</Cell>
                        <Cell align="left">failure Details</Cell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {map(data?.data?.subscriptionPaymentHistory, (item) => (
                        <TableRow
                          sx={{
                            "&.MuiTableRow-head th": { p: "12px" },
                          }}
                        >
                          <TableBodyCell align="left" style={{ width: "25%" }}>
                            {matchDate(item?.paymentScheduleDate)}
                          </TableBodyCell>
                          <TableBodyCell align="right" style={{ width: "25%" }}>
                            {moneyCommaSeparator(item?.paymentAmount || 0)}
                          </TableBodyCell>
                          <TableBodyCell align="left" style={{ width: "25%" }}>
                            <StatusField statusString={item?.paymentStatus} />
                          </TableBodyCell>
                          <TableBodyCell
                            align="left"
                            style={{ width: "25%" }}
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {item?.failureDetails || "-"}
                          </TableBodyCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid2>
              </Grid2>
            </Show.When>
          </Grid2>
        </Show.When>
      </>
    </GKModal>
  );
};

export default MandateFormModal;
